import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ElementRef,
  ViewChild,
  NgZone,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PrnService } from '../prn-moduel.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  Subject,
  debounceTime,
  switchMap,
  tap,
  of,
  catchError,
  Observable,
} from 'rxjs';
import { PoService } from '../../po/po.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { SearchSelectComponent } from '../../../shared/components/search-select/search-select.component';
import { VendorService } from '../../vendors/vendor.service';
import { ProductsService } from '../../products/products.service';
import { distinctUntilChanged, map, filter } from 'rxjs';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import {
  WarehouseSelectComponent,
  WarehouseOption,
} from '../../../shared/components/warehouse-select/warehouse-select.component';
import { QtyInputDirective } from '../../../shared/directives/qty-input.directive';
import { allowsDecimalQty } from '../../../shared/utils/quantity.util';
import { pricingForLine } from '../../../shared/utils/basic-cost.util';
import { currentEanOf, withMatchedEan } from '../../../core/utils/current-ean.util';
import {
  hasPurchasePrice,
  priceNotSetMessage,
} from '../../../core/utils/price-status.util';
import { AuthService } from '../../../auth/auth.service';
import { FormDraftStore } from '../../../shared/utils/form-draft-store';

/** The confirm dialog renders its message as HTML. */
function escapeHtml(text: string): string {
  return String(text).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}

/** What the browser safety copy of an unsaved PRN holds (see autosaveDraft). */
interface PrnDraftSnapshot {
  selectedVendorId: string | null;
  /** The vendor row itself, so the picker can label it before the list loads. */
  vendor: any;
  selectedPoNumber: string | null;
  selectedPoData: any;
  invoiceNumber: string;
  invoiceDate: string;
  returnDate: string;
  freightCharges: number;
  packingCharges: number;
  otherDiscounts: number;
  overallDiscountAmount: number;
  /** The summary round-off box is editable; recomputing would undo the edit. */
  roundOff: number;
  finalTotal: number;
  prnItems: any[];
}

@Component({
  selector: 'app-prn',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    NgSelectModule,
    SearchSelectComponent,
    WarehouseSelectComponent,
    QtyInputDirective,
  ],
  templateUrl: './create-prn.component.html',
  styleUrls: ['./create-prn.component.scss'],
})
export class CreatePrnComponent implements OnInit, OnDestroy {
  /**
   * Whether this line may be keyed in fractions - bulk, weighable, bulk-child
   * or a kg/gm/ltr unit. Counted SKUs (PCS, NOS, PKT ...) stay whole. The
   * server applies the same rule on save.
   */
  allowsDecimal(source: any): boolean {
    return allowsDecimalQty(source);
  }

  private prnService = inject(PrnService);
  private toastr = inject(ToastrService);
  private router = inject(Router);
  private poService = inject(PoService);
  private vendorService = inject(VendorService);
  private productService = inject(ProductsService);
  private route = inject(ActivatedRoute);
  private confirmDialog = inject(ConfirmDialogService);
  private sanitizer = inject(DomSanitizer);
  private authService = inject(AuthService);
  private zone = inject(NgZone);
  private isFormPristine: boolean = false;

  // Preview Logic
  showPreview = false;
  previewUrl: SafeResourceUrl | null = null;
  private rawBlobUrl: string | null = null;
  isLoadingPreview = false;
  private _selectedQuantity: number = 1;
  prnId: string = '';
  prnNumber: string = '';
  // Per-document warehouse picker (multi-warehouse): storeId goes on the PRN
  // payload so the return debits the chosen warehouse.
  selectedWarehouse: WarehouseOption | null = null;

  onWarehouseChange(warehouse: WarehouseOption | null) {
    this.selectedWarehouse = warehouse;
    if (!this.prnId) {
      this.prnService
        .getNextPrnNumber(warehouse?.storeId ?? undefined)
        .subscribe({
          next: (res: any) => {
            this.prnNumber = res?.prnNumber || this.prnNumber;
          },
          error: () => {},
        });
    }
  }

  poWithoutGrnOptions: { poNumber: string; poId: string }[] = [];

  enableProductSearch: Boolean = true;
  poSearch$ = new Subject<string>();

  isPoSearching = false;
  selectedPoNumber: string | null = null;
  poOptions: Array<{ id: string; poNumber: string }> = [];
  selectedPoData: any | null = null;
  selectedPoId: string | null = null;
  prnItems: any[] = [];

  freightCharges: number = 0;
  packingCharges: number = 0;
  todayDate = new Date().toISOString().split('T')[0];
  invoiceNumber: string = '';
  invoiceDate: string = this.todayDate;
  returnDate: string = '';
  otherDiscounts: number = 0;
  products: any[] = [];
  isSearching = false;
  productSearch$ = new Subject<string>();
  selectedProduct: any = null;
  selectedVariant: any = null;

  enteredCostPrice: number = 0;
  allCostPrices: number[] = [];
  allMRPs: number[] = [];
  selectedMRP: number = 0;
  /**
   * The product's live selling MRP — the most recently updated active price row.
   * Flagged in the dropdown so returning against an older printed label reads as
   * a deliberate choice rather than an accident.
   */
  currentMRP: number = 0;

  vendors: any[] = [];
  selectedVendorId: any | null = null;
  vendorSearch$ = new Subject<string>();
  isVendorLoading = false;
  viewOnly = false;

  productCodeSearch$ = new Subject<string>();
  searchResults: any[] = [];
  isCodeSearching = false;
  /** "Search by EAN" box — resolves identity barcodes and price-generation EANs. */
  productEanSearch$ = new Subject<string>();
  eanSearchResults: any[] = [];
  isEanSearching = false;
  selectedSearchType: 'sku' | 'plu' | 'ean' | 'query' = 'sku';
  searchTypes = [
    { value: 'sku', label: 'SKU' },
    { value: 'plu', label: 'PLU' },
    { value: 'ean', label: 'EAN' },
    { value: 'query', label: 'General' },
  ];

  prnSummary = {
    subTotal: 0,
    gstAmount: 0,
    cessAmount: 0,
    schemeDiscountAmount: 0,
    fixedDiscountAmount: 0,
    otherDiscounts: 0,
    freightCharges: 0,
    packingCharges: 0,
    otherCharges: 0,
    grandTotal: 0,
    overallDiscountAmount: 0,
    roundOff: 0,
    finalTotal: 0,
    returnedQuantity: 0,
  };
  overallDiscountAmount: number = 0;
  isEdit: boolean = false;
  soh: number = 0;

  prnData = {
    prnNumber: '',
    purchaseOrderId: '',
    vendorId: '',
    invoiceNumber: '',
    invoiceDate: '',
    returnDate: '',
    subTotalAmount: 0,
    schemeDiscountAmount: 0,
    fixedDiscountAmount: 0,
    overallDiscountAmount: 0,
    gstAmount: 0,
    cessAmount: 0,
    otherCharges: 0,
    freightCharges: 0,
    packingCharges: 0,
    roundOffAmount: 0,
    totalAmount: 0,
    totalQty: 0,
    totalItems: 0,
    remarks: '',
    status: 'DRAFT',
    items: [],
  };

  // Bulk upload state (mirrors the PO flow)
  isUploading = false;
  uploadErrors: Array<{ row: number; code: string; error: string }> = [];

  /** Page heading driven by how the screen was opened. */
  get pageTitle(): string {
    if (this.viewOnly) return 'View PRN';
    return this.isEdit ? 'Edit PRN' : 'Create PRN';
  }

  // ── Line grid helpers (design: collapsible header, filter pills, per-row
  //    delete, clear lines, value strip). UI only — no business logic. ────────
  headerOpen = true;
  lineFilter: 'all' | 'selected' | 'discounted' = 'all';
  lineQuery = '';

  get selectedVendorName(): string {
    const id = this.selectedVendorId;
    if (!id) return '';
    const key = typeof id === 'object' ? id?.id : id;
    return (
      this.vendors.find((v) => v.id === key)?.name ||
      this.selectedPoData?.vendor?.name ||
      ''
    );
  }

  /** True when any discount has been keyed on the line. */
  private lineHasDiscount(item: any): boolean {
    return (
      Number(item?.discountOneAmount || 0) > 0 ||
      Number(item?.discountTwoAmount || 0) > 0 ||
      Number(item?.cashDiscountAmount || 0) > 0 ||
      Number(item?.schemeDiscountAmount || 0) > 0 ||
      Number(item?.fixedDiscountAmount || 0) > 0 ||
      Number(item?.overallDiscountAmount || 0) > 0
    );
  }

  /** Rows shown in the grid after the All / Selected / Discounted pills and the text filter. */
  get visiblePrnItems(): { item: any; index: number }[] {
    const q = (this.lineQuery || '').trim().toLowerCase();
    const out: { item: any; index: number }[] = [];
    this.prnItems.forEach((item, index) => {
      if (this.lineFilter === 'selected' && !this.selectedItems.has(item)) return;
      if (this.lineFilter === 'discounted' && !this.lineHasDiscount(item)) return;
      if (q) {
        const hay = `${item?.skuCode || ''} ${item?.description || ''} ${item?.eanCode || ''} ${item?.pluCode || ''}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      out.push({ item, index });
    });
    return out;
  }

  get discountedCount(): number {
    return this.prnItems.filter((i) => this.lineHasDiscount(i)).length;
  }

  get prnLineCountLabel(): string {
    const shown = this.visiblePrnItems.length;
    const all = this.prnItems.length;
    const word = all === 1 ? 'line' : 'lines';
    return shown === all ? `${all} ${word}` : `${shown} of ${all} lines`;
  }

  get totalFreeQty(): number {
    return this.prnItems.reduce((n, i) => n + Number(i?.freeQuantity || 0), 0);
  }

  /** Sum of the per-line total values (the sheet's TOTAL row). */
  get totalLineValue(): number {
    return this.prnItems.reduce((n, i) => n + Number(i?.totalPrice || 0), 0);
  }

  /** Line + document discounts — what the value strip shows as DISCOUNT. */
  get discountTotal(): number {
    return Number(
      this.prnSummary.overallDiscountAmount || this.overallDiscountAmount || 0,
    );
  }

  get chargesTotal(): number {
    return (
      Number(this.prnSummary.freightCharges || 0) +
      Number(this.prnSummary.packingCharges || 0) +
      Number(this.prnSummary.otherCharges || 0)
    );
  }

  clearLineFilters(): void {
    this.lineFilter = 'all';
    this.lineQuery = '';
  }

  removePrnLine(item: any): void {
    if (this.viewOnly) return;
    const idx = this.prnItems.indexOf(item);
    if (idx < 0) return;
    this.removeItem(idx);
  }

  async clearPrnLines(): Promise<void> {
    if (this.viewOnly || !this.prnItems.length) return;
    const n = this.prnItems.length;
    const proceed = await this.confirmDialog.confirm({
      title: `Clear all ${n} line${n === 1 ? '' : 's'}?`,
      message:
        'Return quantities, free quantities, discounts and pricing keyed on these lines will be removed.',
      confirmText: 'Clear lines',
      cancelText: 'Cancel',
      tone: 'neutral',
      icon: 'bi-eraser',
    });
    if (!proceed) return;
    this.prnItems = [];
    this.selectedItems.clear();
    this.clearLineFilters();
    this.updatePrnSummary();
  }

  ngOnInit(): void {
    this.viewOnly = this.route.snapshot.queryParamMap.get('view') === 'true';
    this.enableProductSearch = !this.viewOnly;
    this.route.params.subscribe((params) => {
      const prnId = params['id'];
      if (prnId) {
        this.prnId = prnId;
        this.isEdit = true;
        this.initDraft(prnId); // restore is offered once the PRN has loaded
        this.loadPrnData(prnId);
      } else {
        this.initDraft(null);
        // After the first render: the restore question is a modal.
        setTimeout(() => this.offerDraftRestore());
        this.prnService.getNextPrnNumber().subscribe({
          next: (res: any) => {
            this.prnNumber = res?.prnNumber || '';
          },
          error: (err) => {
            this.toastr.error('Failed to fetch next GRN number', 'Error');
            console.error('Error fetching next GRN number', err);
          },
        });
      }
    });

    this.poSearch$
      .pipe(
        debounceTime(300),
        tap(() => (this.isPoSearching = true)),
        switchMap((term) => {
          if (term?.length >= 3) {
            return this.poService.searchPo(term).pipe(
              tap(() => (this.isPoSearching = false)),
              catchError(() => {
                this.isPoSearching = false;
                return of([]);
              }),
            );
          } else {
            this.isPoSearching = false;
            return of([]);
          }
        }),
      )
      .subscribe((results: any) => {
        this.poOptions = results;
      });

    this.vendorService.getAllVendorsList().subscribe((res: any) => {
      this.vendors = res || [];
    });

    this.productSearch$
      .pipe(
        debounceTime(300),
        tap(() => (this.isSearching = true)),
        switchMap((term) => {
          const trimmedTerm = term?.trim();
          if (!this.selectedVendorId) {
            this.toastr.warning('Please select a vendor before searching.');
            this.isSearching = false;
            return of([]); // Stop API call
          }
          if (trimmedTerm?.length >= 3) {
            return this.productService
              // Scope to the selected vendor — a PRN returns goods to the
              // vendor they were bought from, so only their products are
              // valid here (backend filters on the PRN context).
              .searchProducts({
                query: trimmedTerm,
                vendorId: this.selectedVendorId,
                context: 'PRN',
              })
              .pipe(
                map((res: any) => res.data || []),
                tap(() => (this.isSearching = false)),
                catchError(() => {
                  this.isSearching = false;
                  return of([]);
                }),
              );
          } else {
            this.isSearching = false;
            return of([]);
          }
        }),
      )
      .subscribe((results: any) => {
        this.products = results || [];
      });

    this.productCodeSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.length >= 2),
        tap(() => (this.isCodeSearching = true)),
        switchMap((term) => {
          const trimmedTerm = term?.trim();
          if (!this.selectedVendorId) {
            this.toastr.warning('Please select a vendor before searching.');
            this.isCodeSearching = false;
            return of([]); // Stop API call
          }
          let sku: string | undefined;
          let plu: string | undefined;
          let ean: string | undefined;

          switch (this.selectedSearchType) {
            case 'sku':
              sku = trimmedTerm;
              break;
            case 'plu':
              plu = trimmedTerm;
              break;
            case 'ean':
              ean = trimmedTerm;
              break;
            default:
              sku = trimmedTerm;
          }

          return this.productService
            .searchProducts({
              sku,
              plu,
              ean,
              vendorId: this.selectedVendorId,
              context: 'PRN',
            })
            .pipe(
              map((res: any) => res.data || []),
              tap(() => (this.isCodeSearching = false)),
              catchError(() => {
                this.isCodeSearching = false;
                return of([]);
              }),
            );
        }),
      )
      .subscribe((data: any) => {
        this.searchResults = data.map((item: any) => ({
          ...item,
          ean: currentEanOf(item.variants?.[0]),
          displayText: `${item.sku} - ${item.longDescription}`,
        }));
        this.isCodeSearching = false;
      });

    this.productEanSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => !!term && term.trim().length >= 3),
        tap(() => (this.isEanSearching = true)),
        switchMap((term) => {
          if (!this.selectedVendorId) {
            this.toastr.warning('Please select a vendor before searching.');
            this.isEanSearching = false;
            return of([]);
          }
          // Vendor-scoped like the SKU / name searches above.
          return this.productService
            .searchProducts({
              ean: term.trim(),
              vendorId: this.selectedVendorId,
              context: 'PRN',
            })
            .pipe(
              map((res: any) => withMatchedEan(res?.data || [], term)),
              tap(() => (this.isEanSearching = false)),
              catchError(() => {
                this.isEanSearching = false;
                return of([]);
              }),
            );
        }),
      )
      .subscribe((data: any) => {
        this.eanSearchResults = data;
        this.isEanSearching = false;
      });

    this.poService.getPurchaseOrdersWithoutGrn().subscribe({
      next: (res: any) => {
        this.poWithoutGrnOptions = res;
        this.poOptions = res.map((po: any) => ({
          poNumber: po.poNumber,
          id: po.poId,
        }));
      },
      error: (err) => {
        console.error('Error loading POs without GRN', err);
      },
    });
  }
  @ViewChild('quantityInput') quantityInputRef!: ElementRef;

  ngOnDestroy(): void {
    if (this.draftTimer) clearInterval(this.draftTimer);
    this.autosaveDraft();
  }

  loadPrnData(prnId: string) {
    this.prnService.getPrnById(this.prnId).subscribe({
      next: (data: any) => {
        this.selectedPoData = {
          id: data.purchaseOrderId,
          poNumber: data.poNumber || data.purchaseOrder?.poNumber || '',
          vendor: data.vendor || data.vendorId,
        };

        this.selectedPoNumber =
          data.poNumber || data.purchaseOrder?.poNumber || '';

        this.selectedVendorId = data.vendorId || data.vendor?.id || '';
        this.prnNumber = data.prnNumber;
        this.invoiceDate = data.invoiceDate?.split('T')[0] || this.todayDate;
        this.invoiceNumber = data.invoiceNumber;
        this.returnDate = data.invoiceDate?.split('T')[0] || '';
        this.packingCharges = data.packingCharges || 0;
        this.freightCharges = data.freightCharges || 0;
        // this.otherDiscounts = data.otherDiscountAmount || 0;
        this.overallDiscountAmount = data.overallDiscountAmount || 0;

        this.prnItems = data.items.map((item: any) => ({
          productVariantId: item.productVariantId,
          skuCode: item.skuCode,
          description: item.productVariant.product.longDescription,
          uom: item.uom,
          eanCode: currentEanOf(item.productVariant),
          innerQuantity: item.innerQuantity,
          poQuantity: item.poQuantity,
          receivedQuantity: item.receivedQuantity,
          returnedQuantity: item.returnedQuantity,
          freeQuantity: item.freeQuantity,
          costPrice: item.costPrice,
          actualCostPrice: item.actualCostPrice,
          gstPercent: item.gstPercent,
          gstAmount: item.gstAmount,
          cessRate: item.cessRate,
          cessAmount: item.cessAmount,
          mrp: item.mrp,
          // The GRN line's own price row (same cost + MRP), not the newest one.
          sellingPrice: (
            pricingForLine(item?.productVariant?.pricing, item.costPrice, item.mrp) ??
            item?.productVariant?.pricing?.[0]
          )?.sellingPrice,
          freightCharges: item.freightCharges,
          totalPrice: +(item.costPrice * item.returnedQuantity).toFixed(2),
          discountOnePercent: item.discountOnePercent,
          discountOneAmount: item.discountOneAmount,
          discountTwoPercent: item.discountTwoPercent,
          discountTwoAmount: item.discountTwoAmount,
          cashDiscountPercent: item.cashDiscountPercent,
          cashDiscountAmount: item.cashDiscountAmount,
          schemeDiscountAmount: item.schemeDiscountAmount,
          fixedDiscountAmount: item.fixedDiscountAmount,
          overallDiscountAmount: item.overallDiscountAmount,
          oldRoiPercent: item.oldRoiPercent,
          newRoiPercent: item.newRoiPercent,
          hsnCode: item.productVariant.product.hsnCode,
          id: item.id,
          packingCharges: item.packingCharges || 0,
          pricing: item.productVariant.pricing,
        }));

        this.updatePrnSummary();
        this.offerDraftRestore(data?.updatedAt);
      },
      error: (err) => {
        this.toastr.error('Failed to load GRN data', 'Error');
        console.error('Error loading GRN:', err);
      },
    });
  }

  onPoIdChange(selectedId: string) {
    const found = this.poWithoutGrnOptions.find((po) => po.poId === selectedId);
    if (found) {
      this.selectedPoNumber = found.poNumber;

      this.poService.getPoById(found.poId).subscribe({
        next: (po: any) => this.onPoSelected(po),
        error: (err) => console.error('Error fetching PO by ID', err),
      });
    }
  }

  onVendorChange() {
    this.uploadErrors = [];
    // Product search/template are vendor-scoped — drop anything stale.
    this.products = [];
    this.searchResults = [];
    this.eanSearchResults = [];
    if (!this.selectedPoData) {
      this.prnItems = [];
    }
  }

  // ───────────────────────── Excel template / bulk upload ────────────────────

  downloadTemplate() {
    // Blank template — vendor is only required at upload time.
    this.prnService.downloadTemplate(this.selectedVendorId || undefined).subscribe({
      next: (blob: any) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'prn_template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Template downloaded successfully.');
      },
      error: () => this.toastr.error('Failed to download template.'),
    });
  }

  onTemplateFileSelected(event: any) {
    const file: File = event.target.files?.[0];
    if (!file) return;
    if (!this.selectedVendorId) {
      this.toastr.warning('Please select a vendor first.');
      event.target.value = '';
      return;
    }

    this.isUploading = true;
    this.uploadErrors = [];
    this.prnService.parseUpload(file, this.selectedVendorId).subscribe({
      next: (res: any) => {
        this.isUploading = false;
        this.uploadErrors = res.errors || [];
        const rows: any[] = res.rows || [];

        let added = 0;
        let skipped = 0;
        for (const row of rows) {
          const item = this.buildPrnItemFromUpload(row.product, row.quantity);
          if (!item) {
            skipped++;
            continue;
          }
          const existing = this.prnItems.find(
            (i) => i.productVariantId === item.productVariantId,
          );
          if (existing) {
            // Same SKU twice (file row + already on screen) — add the qty
            // rather than silently overwriting the operator's entry.
            existing.returnedQuantity =
              (+existing.returnedQuantity || 0) + (+item.returnedQuantity || 0);
            this.onReturnedQuantityChange(existing);
          } else {
            this.prnItems.push(item);
          }
          added++;
        }

        this.updatePrnSummary();
        if (added > 0)
          this.toastr.success(`${added} item(s) loaded from file.`);
        if (skipped > 0)
          this.toastr.warning(`${skipped} row(s) skipped — no pricing found.`);
        if (this.uploadErrors.length > 0)
          this.toastr.warning(
            `${this.uploadErrors.length} row(s) had errors — see details below.`,
          );
        if (added === 0 && this.uploadErrors.length === 0)
          this.toastr.info('No quantities found in the file.');
        event.target.value = '';
      },
      error: (err: any) => {
        this.isUploading = false;
        this.toastr.error(err?.error?.message || 'Upload failed.');
        event.target.value = '';
      },
    });
  }

  /**
   * Build a PRN line from an uploaded row. Uses the same cost/GST maths as a
   * manual add (confirmAddToTable) so both paths always agree.
   */
  private buildPrnItemFromUpload(product: any, qty: number): any | null {
    const variant = product?.variants?.[0];
    const pricing = variant?.pricing?.[0];
    // No usable price yet (barcode tagged, pricing deferred): skip rather
    // than return goods at ₹0. The caller reports the skipped count.
    if (!variant || !pricing || !hasPurchasePrice(variant)) return null;

    const gstRate = parseFloat(product?.purchaseGstRate?.value || '0') || 0;
    const costPrice = +pricing.costPrice || 0;
    const actualCost = +(costPrice / (1 + gstRate / 100)).toFixed(4);
    const gstAmount = +((costPrice - actualCost) * qty).toFixed(2);
    const total = +(costPrice * qty).toFixed(2);
    const cessRate = +product?.cessRate || 0;

    return {
      productVariant: variant,
      productVariantId: variant.id,
      skuCode: product?.sku || '',
      pluCode: '',
      description: product?.longDescription || product?.name || '',
      eanCode: currentEanOf(variant),
      innerQuantity: product?.innerPackaging || 1,
      uom: product?.uom || '',
      // Decides whether this line's quantity box accepts decimals: a
      // kg/gm/ltr unit, or a bulk / weighable / bulk-child product.
      isBulk: product?.isBulk ?? null,
      isWeighable: product?.isWeighable ?? null,
      bulkParentProductId: product?.bulkParentProductId ?? null,
      poQuantity: qty,
      receivedQuantity: qty,
      returnedQuantity: qty,
      freeQuantity: 0,
      actualCostPrice: actualCost,
      gstPercent: gstRate,
      gstAmount,
      costPrice,
      mrp: +pricing.mrp || 0,
      sellingPrice: +pricing.sellingPrice || 0,
      freightCharges: 0,
      packingCharges: 0,
      totalPrice: total,
      discountOnePercent: 0,
      discountOneAmount: 0,
      discountTwoPercent: 0,
      discountTwoAmount: 0,
      cashDiscountPercent: 0,
      cashDiscountAmount: 0,
      schemeDiscountAmount: 0,
      fixedDiscountAmount: 0,
      overAllDiscountAmount: 0,
      oldRoiPercent: 0,
      newRoiPercent: 0,
      hsnCode: product?.hsnCode || '',
      cessRate,
      cessAmount: +((costPrice * cessRate) / 100).toFixed(2),
    };
  }

  onPoSelected(po: any) {
    if (po) {
      this.selectedPoNumber = po.poNumber;
      this.selectedPoData = po;
      this.prnItems = po?.items.map((item: any) => {
        const variant = item.productVariant;
        const product = variant?.product || {};
        const costPrice = +item.costPrice || 0;
        const gstPercent = +item.gstPercent || 0;
        const basicCostPrice = +(costPrice / (1 + gstPercent / 100)).toFixed(4);
        return {
          productVariant: item.productVariant,
          productVariantId: item.productVariantId,
          skuCode: product?.sku || '',
          pluCode: '', // to confirm
          description: product?.longDescription || '',
          eanCode: currentEanOf(variant),
          innerQuantity: product?.innerPackaging || 1,
          uom: product?.uom || '',
          // Decides whether this line's quantity box accepts decimals: a
          // kg/gm/ltr unit, or a bulk / weighable / bulk-child product.
          isBulk: product?.isBulk ?? null,
          isWeighable: product?.isWeighable ?? null,
          bulkParentProductId: product?.bulkParentProductId ?? null,
          poQuantity: item.quantity,
          receivedQuantity: item.quantity,
          returnedQuantity: item.returnedQuantity || 0,
          freeQuantity: item.freeQuantity || 0,
          actualCostPrice: basicCostPrice,
          gstPercent: +item.gstPercent,
          gstAmount: item.gstAmount,
          costPrice: +item.costPrice,
          cessRate: item.cessRate,
          cessAmount: item.cessAmount,
          mrp: +item.mrp || 0,
          // PO lines carry no selling price: use the PO line's own price row.
          sellingPrice:
            (pricingForLine(variant?.pricing, item.costPrice, item.mrp) ?? variant?.pricing?.[0])
              ?.sellingPrice || 0,
          freightCharges: 0,
          packingCharges: 0,
          totalPrice: +(costPrice * item.quantity).toFixed(2),
          discountOnePercent: 0,
          discountOneAmount: 0,
          discountTwoPercent: 0,
          discountTwoAmount: 0,
          cashDiscountPercent: 0,
          cashDiscountAmount: 0,
          schemeDiscountAmount: 0,
          fixedDiscountAmount: 0,
          overallDiscountAmount: 0,
          oldRoiPercent: item.oldRoiPercent, // fetch later
          newRoiPercent: +(
            ((item.mrp - item.costPrice) / item.mrp) *
            100
          ).toFixed(2),
          hsnCode: product?.hsnCode || '',
          id: item?.id,
        };
      });
      this.updatePrnSummary();
    }
  }

  updatePrnSummary() {
    let subTotal = 0;
    let gst = 0;
    let cess = 0;
    let scheme = 0;
    let fixed = 0;
    let other = 0;

    let freight = +this.freightCharges || 0;
    let packing = +this.packingCharges || 0;
    let otherCharges = 0;
    let returnedQuantity = this.prnItems.reduce(
      (sum, item) => sum + (+item.returnedQuantity || 0),
      0,
    );

    for (const item of this.prnItems) {
      const qty = +item.receivedQuantity || +item.returnedQuantity || 0;
      const basic = +item.actualCostPrice || 0;
      const gstAmt = +item.gstAmount || 0;
      const cessAmt = +item.cessAmount || 0;
      subTotal += basic * qty;
      gst += gstAmt;
      cess += cessAmt;

      scheme += +item.schemeDiscountAmount || 0;
      fixed += +item.fixedDiscountAmount || 0;
      other +=
        (+item.discountOneAmount || 0) +
        (+item.discountTwoAmount || 0) +
        (+item.cashDiscountAmount || 0);
    }

    const totalBeforeTax = subTotal;
    const overallDiscount = this.prnSummary.otherDiscounts || 0;
    const overallDiscountAmount = scheme + fixed + other + overallDiscount;

    const grand =
      +subTotal +
      +gst +
      +otherCharges +
      +freight +
      +packing -
      +overallDiscountAmount;

    const roundOff = +(Math.round(grand) - grand).toFixed(2);
    const finalTotal = Math.round(grand);

    this.prnSummary = {
      subTotal: totalBeforeTax,
      gstAmount: gst,
      cessAmount: cess,
      schemeDiscountAmount: scheme,
      fixedDiscountAmount: fixed,
      otherDiscounts: overallDiscount,
      overallDiscountAmount: overallDiscountAmount,
      freightCharges: freight,
      packingCharges: packing,
      otherCharges,
      grandTotal: grand,
      roundOff,
      finalTotal,
      returnedQuantity,
    };
  }

  onVendorSelected(vendorId: string) {
    this.selectedVendorId = vendorId;

    this.onVendorChange();
  }

  onVendorSearch(event: { term?: string; items?: any[] }) {
    const trimmed = event?.term?.trim?.();
    if (trimmed) {
      this.vendorSearch$.next(trimmed);
    }
  }

  onOtherDiscountsChange() {
    this.prnSummary.otherDiscounts = Number(this.otherDiscounts) || 0;
    this.updatePrnSummary();
  }

  onProductSelected(product: any) {
    this.selectedProduct = product;
    this.selectedVariant = product?.variants?.[0];
    this.selectedQuantity = product?.innerPackaging || 1;
    this.enteredCostPrice = +this.selectedVariant?.pricing?.[0]?.costPrice || 0;
    this.selectedMRP = +this.selectedVariant?.pricing?.[0]?.mrp || 0;
    this.warnIfUnpriced(product, this.selectedVariant);
    this.populatePriceOptions(product);

    this.soh = product.variants?.[0]?.soh || 0;

    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    });
  }

  customSearchFn(term: string, item: any): boolean {
    term = term.toLowerCase();
    return (
      item?.longDescription?.toLowerCase()?.includes(term) ||
      item?.skuCode?.toLowerCase()?.includes(term)
    );
  }

  confirmAddToTable() {
    if (
      !this.selectedProduct ||
      !this.selectedQuantity ||
      !this.enteredCostPrice
    )
      return;

    if (this.selectedQuantity > this.soh) {
      console.log(`error should come this.soh : ${this.soh}`);
      this.toastr.error(
        'Cannot add this item. The returned quantity exceeds the available stock.',
      );
      return;
    }

    // One SKU may appear on several lines as long as the cost price differs
    // (a return can carry stock bought at different prices). Only an exact
    // SKU + cost price repeat is blocked.
    const duplicate = this.prnItems.find(
      (item) =>
        item.skuCode === this.selectedProduct?.sku &&
        +item.costPrice === +this.enteredCostPrice,
    );

    if (duplicate) {
      this.toastr.error(
        'This SKU is already in the list at this cost price. Update its quantity instead.',
      );
      return; // Stop adding
    }

    // The pricing row the user actually picked in the MRP dropdown. This used to
    // read pricing[0] regardless, so choosing MRP 110 on a product whose most
    // recent price row was 100 silently returned the line at 100.
    const selectedPricing = this.pricingForSelectedMrp();

    const proceed = () => {
      const gstRate = parseFloat(
        this.selectedProduct?.purchaseGstRate?.value || '0',
      );
      const costPrice = +this.enteredCostPrice;
      const actualCost = +(costPrice / (1 + gstRate / 100)).toFixed(4);
      const gstAmount = +(
        (costPrice - actualCost) *
        this.selectedQuantity
      ).toFixed(2);
      const total = +(costPrice * this.selectedQuantity).toFixed(2);

      this.prnItems.push({
        productVariant: this.selectedVariant,
        productVariantId: this.selectedVariant.id,
        skuCode: this.selectedProduct?.sku || '',
        pluCode: '',
        description: this.selectedProduct?.longDescription || '',
        eanCode: currentEanOf(this.selectedVariant),
        innerQuantity: this.selectedProduct?.innerPackaging || 1,
        uom: this.selectedProduct?.uom || '',
        // Decides whether this line's quantity box accepts decimals: a
        // kg/gm/ltr unit, or a bulk / weighable / bulk-child product.
        isBulk: this.selectedProduct?.isBulk ?? null,
        isWeighable: this.selectedProduct?.isWeighable ?? null,
        bulkParentProductId: this.selectedProduct?.bulkParentProductId ?? null,
        poQuantity: this.selectedQuantity,
        receivedQuantity: this.selectedQuantity,
        returnedQuantity: this.selectedQuantity,
        freeQuantity: 0,
        actualCostPrice: actualCost,
        gstPercent: gstRate,
        gstAmount,
        costPrice,
        mrp: +selectedPricing?.mrp || 0,
        sellingPrice: +selectedPricing?.sellingPrice || 0,
        freightCharges: 0,
        packingCharges: 0,
        totalPrice: total,
        discountOnePercent: 0,
        discountOneAmount: 0,
        discountTwoPercent: 0,
        discountTwoAmount: 0,
        cashDiscountPercent: 0,
        cashDiscountAmount: 0,
        schemeDiscountAmount: 0,
        fixedDiscountAmount: 0,
        overAllDiscountAmount: 0,
        oldRoiPercent: 0,
        newRoiPercent: 0,
        hsnCode: this.selectedProduct?.hsnCode || '',
        cessRate: this.selectedProduct.cessRate,
        cessAmount: (costPrice * this.selectedProduct.cessRate) / 100,
      });

      this.selectedProduct = null;
      this.selectedQuantity = 1;
      this.enteredCostPrice = 0;
      this.selectedMRP = 0;
      this.updatePrnSummary();
    };

    // Raising a PRN no longer writes to the product master. It used to call
    // addVariantPricing whenever the typed cost price didn't match an existing
    // row, so every return at a negotiated price permanently added another
    // active MRP to the product — which is how the MRP dropdown filled up with
    // near-duplicate prices in the first place. PRNItem.costPrice is a plain
    // column with no FK, so the typed price rides on the line by itself.
    proceed();
  }

  /**
   * The pricing row behind the MRP currently chosen in the dropdown. Prefers the
   * selected variant's own rows, widens to the product's other variants (the
   * dropdown aggregates across them), and falls back to the current price.
   */
  private pricingForSelectedMrp(): any | null {
    const rows = this.selectedVariant?.pricing || [];
    return (
      rows.find((p: any) => +p.mrp === +this.selectedMRP) ??
      this.findPricingByMRP(this.selectedMRP) ??
      rows[0] ??
      null
    );
  }

  onCostPriceChange(item: any) {
    // Editing the cost price on a PRN line is a negotiation with the vendor, not
    // a change to the product master. This used to create a VariantPricing row
    // for any price not already on file; it no longer touches the master at all.
    this.onPriceChange(item, 'costPrice');
  }

  onPriceChange(item: any, changedKey: string) {
    const costPrice = +item.costPrice || 0;
    const gstPercent = +item.gstPercent || 0;
    const qty = +item.receivedQuantity || item.quantity || 0;
    const freight = +item.freightCharges || 0;
    let discountAmount: any;

    const totalBeforeDiscount = costPrice * qty;
    const basic = +(costPrice / (1 + gstPercent / 100)).toFixed(4);
    const cess = +((costPrice * (item.cessPercent || 0)) / 100).toFixed(2);

    if (changedKey === 'discountOnePercent') {
      item.discountOneAmount = +(
        (totalBeforeDiscount * item.discountOnePercent) /
        100
      ).toFixed(2);
    } else if (changedKey === 'discountOneAmount') {
      discountAmount =
        totalBeforeDiscount - qty * (costPrice - item.discountOneAmount);
      item.discountOnePercent = +(
        (item.discountOneAmount / totalBeforeDiscount) *
        100
      ).toFixed(2);
    }

    const baseForDiscountTwo =
      totalBeforeDiscount - (+item.discountOneAmount || 0);

    if (changedKey === 'discountTwoPercent') {
      item.discountTwoAmount = +(
        (baseForDiscountTwo * item.discountTwoPercent) /
        100
      ).toFixed(2);
    } else if (changedKey === 'discountTwoAmount') {
      let discountAmountTwo =
        totalBeforeDiscount -
        qty * (costPrice - (item.discountOneAmount + item.discountTwoAmount));
      discountAmount = discountAmountTwo;

      item.discountTwoPercent = +(
        (item.discountTwoAmount / baseForDiscountTwo) *
        100
      ).toFixed(2);
    }

    if (changedKey === 'cashDiscountPercent') {
      item.cashDiscountAmount = +(
        (totalBeforeDiscount * item.cashDiscountPercent) /
        100
      ).toFixed(2);
    } else if (changedKey === 'cashDiscountAmount') {
      item.cashDiscountPercent = +(
        (item.cashDiscountAmount / totalBeforeDiscount) *
        100
      ).toFixed(2);
    }

    let totalDiscount =
      +item.discountOneAmount +
      +item.discountTwoAmount +
      +item.cashDiscountAmount;

    if (discountAmount) {
      item.overallDiscountAmount = discountAmount;
    } else {
      item.overallDiscountAmount = totalDiscount;
    }

    item.basicCostPrice = basic;
    item.cessAmount = +(cess * qty).toFixed(2);

    item.totalPrice = +(
      totalBeforeDiscount +
      freight +
      item.cessAmount
    ).toFixed(2);

    if (discountAmount) {
      item.totalPrice = item.totalPrice - discountAmount;
    } else {
      item.totalPrice = item.totalPrice - totalDiscount;
    }

    this.updatePrnSummary();
  }

  removeItem(index: number) {
    this.selectedItems.delete(this.prnItems[index]);
    this.prnItems.splice(index, 1);
    this.updatePrnSummary();
  }

  // Multi-select line items for bulk delete
  selectedItems = new Set<any>();

  isItemSelected(item: any): boolean {
    return this.selectedItems.has(item);
  }

  toggleItemSelection(item: any): void {
    if (this.selectedItems.has(item)) {
      this.selectedItems.delete(item);
    } else {
      this.selectedItems.add(item);
    }
  }

  get selectedCount(): number {
    return this.prnItems.reduce(
      (n: number, it: any) => n + (this.selectedItems.has(it) ? 1 : 0),
      0
    );
  }

  get allItemsSelected(): boolean {
    return this.prnItems.length > 0 && this.selectedCount === this.prnItems.length;
  }

  toggleSelectAll(checked: boolean): void {
    this.selectedItems.clear();
    if (checked) {
      this.prnItems.forEach((it: any) => this.selectedItems.add(it));
    }
  }

  async removeSelectedItems(): Promise<void> {
    const count = this.selectedCount;
    if (!count) return;
    const proceed = await this.confirmDialog.confirm({
      title: 'Delete Selected Items',
      message: `Are you sure you want to delete ${count} selected item${count > 1 ? 's' : ''}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!proceed) return;
    for (let i = this.prnItems.length - 1; i >= 0; i--) {
      if (this.selectedItems.has(this.prnItems[i])) {
        this.prnItems.splice(i, 1);
      }
    }
    this.selectedItems.clear();
    this.updatePrnSummary();
  }

  get totalProducts(): number {
    return this.prnItems?.length || 0;
  }

  get totalReceivedQuantity(): number {
    return (
      this.prnItems?.reduce(
        (sum, item) => sum + Number(item.receivedQuantity || 0),
        0,
      ) || 0
    );
  }

  get totalReturnedQuantity(): number {
    return this.prnItems.reduce(
      (sum, item) =>
        sum + (+item.returnedQuantity || 0) + (+item.freeQuantity || 0),
      0,
    );
  }

  saveAsDraft(): void {
    this.submitPrn('DRAFT');
  }

  async submitPrn(status: 'DRAFT' | 'POSTED' = 'POSTED') {
    const totalQty = this.totalReturnedQuantity;
    const totalItems = this.totalProducts || this.prnItems?.length || 0;

    if (status === 'POSTED') {
      const confirmed = await this.confirmDialog.confirm({
        title: 'Submit PRN?',
        message: `You are about to submit this PRN with <strong>${totalItems}</strong> item(s), total return qty <strong>${totalQty}</strong>, total amount <strong>${this.prnSummary.finalTotal}</strong>.<br/>Do you want to continue?`,
        confirmText: 'Submit PRN',
        cancelText: 'Cancel',
      });
      if (!confirmed) return;
    }

    const prnRequest = {
      vendorId: this.selectedVendorId ?? null,
      // The document number is assigned by the server at create. The number
      // shown on screen is a non-reserving preview, so it is not sent.
      storeId: this.selectedWarehouse?.storeId ?? undefined,
      purchaseOrderId: this.selectedPoData?.id || null,
      invoiceDate: this.invoiceDate
        ? new Date(this.invoiceDate).toISOString()
        : new Date().toISOString(),
      returnDate: this.returnDate
        ? new Date(this.returnDate).toISOString()
        : new Date().toISOString(),
      invoiceNumber: this.invoiceNumber,
      subTotalAmount: Number(this.prnSummary.subTotal),
      gstAmount: this.prnSummary.gstAmount,
      cessAmount: this.prnSummary.cessAmount,
      schemeDiscountAmount: this.prnSummary.schemeDiscountAmount,
      fixedDiscountAmount: this.prnSummary.fixedDiscountAmount,
      overallDiscountAmount: this.prnSummary.overallDiscountAmount,
      //   otherDiscountAmount: this.prnSummary.otherDiscounts,
      freightCharges: Number(this.freightCharges),
      packingCharges: Number(this.packingCharges),
      roundOffAmount: this.prnSummary.roundOff,
      otherCharges: this.prnSummary.otherCharges,
      totalAmount: this.prnSummary.finalTotal,
      status,
      totalQty,
      totalItems,

      items: this.prnItems.map((item) => {
        return {
          productVariantId: item.productVariantId,
          skuCode: item.skuCode,
          uom: item.uom,
          //   eanCode: item.eanCode,
          //   innerQuantity: Number(item.innerQuantity),
          //   poQuantity: Number(item.poQuantity),
          //   receivedQuantity: Number(item.receivedQuantity),
          freeQuantity: Number(item.freeQuantity),
          costPrice: Number(item.costPrice),
          actualCostPrice: Number(item.actualCostPrice),
          gstPercent: Number(item.gstPercent),
          gstAmount: Number(item.gstAmount),
          sgstPercent: Number(item.gstPercent) / 2,
          cgstPercent: Number(item.gstPercent) / 2,
          cessRate: Number(item.cessRate),
          cessAmount: Number(item.cessAmount),
          mrp: Number(item.mrp),
          //   sellingPrice: Number(item.sellingPrice),
          //freightCharges: Number(item.freightCharges),
          // packingCharges: 0,
          //   otherCharges: 0,
          totalPrice: Number(item.totalPrice),
          discountOnePercent: Number(item.discountOnePercent),
          discountOneAmount: Number(item.discountOneAmount),
          discountTwoPercent: Number(item.discountTwoPercent),
          discountTwoAmount: Number(item.discountTwoAmount),
          cashDiscountPercent: Number(item.cashDiscountPercent),
          cashDiscountAmount: Number(item.cashDiscountAmount),
          schemeDiscountAmount: Number(item.schemeDiscountAmount),
          fixedDiscountAmount: Number(item.fixedDiscountAmount),
          overallDiscountAmount: Number(item.overallDiscountAmount),
          //   oldRoiPercent: Number(item.oldRoiPercent),
          //   oldRoiAmount: 0,
          //   newRoiPercent: Number(item.newRoiPercent),
          //   newRoiAmount: 0,
          //  hsnCode: item.hsnCode,
          returnedQuantity: Number(item.returnedQuantity),
        };
      }),
    };

    const request$ = this.prnId
      ? this.prnService.updatePrn(this.prnId, prnRequest)
      : this.prnService.createPrn(prnRequest);

    request$.subscribe({
      next: (res: any) => {
        const msg = this.prnId
          ? 'PRN updated successfully!'
          : 'PRN created successfully!';
        this.toastr.success(msg, 'Success');
        this.discardDraft();
        if (res?.prnNumber) this.prnNumber = res.prnNumber;
        this.isFormPristine = true;
        this.confirmDialog.setFormPristine();

        const createdId =
          res?.id || res?.data?.id || (this.prnId ? this.prnId : null);
        if (createdId) {
          this.loadPreview(createdId);
        } else {
          this.router.navigate(['/prn']);
        }
      },
      error: (err) => {
        const msg = this.prnId
          ? 'Failed to update PRN'
          : 'Failed to create PRN';
        this.toastr.error(msg, 'Error');
      },
    });
  }

  onRoundOffChanged() {
    // rounding it to 2 decimal places
    this.prnSummary.roundOff = +this.prnSummary.roundOff?.toFixed(2) || 0;

    // Recalculate final total
    this.prnSummary.finalTotal =
      (this.prnSummary.subTotal || 0) +
      (this.prnSummary.gstAmount || 0) +
      (this.prnSummary.cessAmount || 0) +
      (this.prnSummary.freightCharges || 0) +
      (this.prnSummary.packingCharges || 0) +
      (this.prnSummary.otherCharges || 0) -
      (this.prnSummary.schemeDiscountAmount || 0) -
      (this.prnSummary.fixedDiscountAmount || 0) -
      (this.prnSummary.otherDiscounts || 0) -
      (this.prnSummary.overallDiscountAmount || 0) +
      (this.prnSummary.roundOff || 0);
  }

  /** A SKU may be created with only a barcode; say so instead of prefilling ₹0. */
  private warnIfUnpriced(product: any, variant: any) {
    if (!product || hasPurchasePrice(variant)) return;
    this.toastr.warning(
      priceNotSetMessage(product.sku, product.longDescription),
      'Price required',
    );
  }

  onSearchResultSelected(product: any) {
    if (!product) return;

    this.selectedProduct = product;
    this.selectedVariant = product.variants?.[0];
    this.selectedQuantity = product?.innerPackaging || 1;
    this.enteredCostPrice = +this.selectedVariant?.pricing?.[0]?.costPrice || 0;
    this.selectedMRP = +this.selectedVariant?.pricing?.[0]?.mrp || 0;
    this.warnIfUnpriced(product, this.selectedVariant);
    this.soh = product.variants?.[0]?.soh || 0;

    // Focus on quantity input after selection
    setTimeout(() => {
      this.quantityInputRef?.nativeElement?.focus();
    }, 100);

    // Show success message
    this.toastr.success(
      `Product found: ${product.longDescription}`,
      'Product Selected',
    );

    // Clear search results after selection
    this.searchResults = [];
    this.eanSearchResults = [];
  }

  // ✅ NEW: Handle search type change (SKU/PLU/EAN/General)
  onSearchTypeChange() {
    this.searchResults = [];
    this.eanSearchResults = [];
    // Clear current selection when search type changes
    if (this.selectedProduct) {
      this.selectedProduct = null;
    }
  }

  // ✅ Called by PendingChangesGuard
  async canDeactivate(): Promise<boolean> {
    // ✅ Check if the form has been successfully saved first
    if (this.isFormPristine) {
      return true;
    }

    // Original logic for view-only mode
    if (this.viewOnly) {
      return true;
    }

    // Use the service's internal state to check for a pristine form
    if (this.confirmDialog.isFormPristine()) {
      return true;
    }

    // Now, run the checks for unsaved changes
    const hasUnsavedChanges =
      this.prnItems?.length > 0 ||
      this.prnId ||
      this.freightCharges > 0 ||
      this.packingCharges > 0 ||
      this.overallDiscountAmount > 0 ||
      this.otherDiscounts > 0;

    if (hasUnsavedChanges) {
      const leave = await this.confirmDialog.confirm({
        title: 'Discard changes?',
        message:
          'You have unsaved changes in <strong>PRN</strong>. Are you sure you want to leave this page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
      // Leaving on purpose discards the browser copy too. Not when this is
      // the sign-out path (no token by now): that copy is the user's way back.
      if (leave && this.authService.isLoggedIn()) this.discardDraft();
      return leave;
    }

    return true;
  }

  // ── Unsaved-work safety copy ────────────────────────────────────────────
  // Same protection as the GRN form (a 140-line GRN was lost to an expired
  // session): the form copies itself to the browser every few seconds
  // (FormDraftStore) and offers the copy back when it is opened again.
  private draftStore: FormDraftStore<PrnDraftSnapshot> | null = null;
  private draftTimer: ReturnType<typeof setInterval> | null = null;
  /** False until the restore question is settled, so the still-blank form
   *  cannot overwrite the copy it is about to offer. */
  private draftReady = false;
  /** Saved to the server, or discarded on purpose: stop copying. */
  private draftClosed = false;

  private initDraft(prnId: string | null): void {
    if (this.viewOnly) return;
    this.draftStore = new FormDraftStore<PrnDraftSnapshot>([
      'prn',
      this.authService.getUserId(),
      this.authService.getActiveLocationId(),
      prnId ?? 'new',
    ]);
    // Outside Angular: the timer must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.draftTimer = setInterval(() => this.autosaveDraft(), 5000);
    });
  }

  private hasDraftContent(d: Partial<PrnDraftSnapshot> | null | undefined): boolean {
    return !!(d?.prnItems?.length || d?.invoiceNumber || d?.selectedVendorId);
  }

  private draftSnapshot(): PrnDraftSnapshot {
    return {
      selectedVendorId: this.selectedVendorId,
      vendor: this.vendors.find((v: any) => v.id === this.selectedVendorId) ?? null,
      selectedPoNumber: this.selectedPoNumber,
      selectedPoData: this.selectedPoData,
      invoiceNumber: this.invoiceNumber,
      invoiceDate: this.invoiceDate,
      returnDate: this.returnDate,
      freightCharges: this.freightCharges,
      packingCharges: this.packingCharges,
      otherDiscounts: this.otherDiscounts,
      overallDiscountAmount: this.overallDiscountAmount,
      roundOff: this.prnSummary.roundOff,
      finalTotal: this.prnSummary.finalTotal,
      prnItems: this.prnItems,
    };
  }

  /** Also on pagehide: a closed tab or browser keeps the last few seconds. */
  @HostListener('window:pagehide')
  autosaveDraft(): void {
    if (!this.draftStore || !this.draftReady || this.draftClosed) return;
    const snapshot = this.draftSnapshot();
    // An emptied form leaves any earlier copy alone ("Not now" kept it).
    if (this.hasDraftContent(snapshot)) this.draftStore.save(snapshot);
  }

  private discardDraft(): void {
    this.draftClosed = true;
    this.draftStore?.clear();
  }

  /**
   * Offer a copy left by an earlier visit. For an existing PRN a copy older
   * than the version on the server is stale and silently dropped.
   */
  private async offerDraftRestore(serverUpdatedAt?: string): Promise<void> {
    const stored = this.draftStore?.load();
    if (!stored || !this.hasDraftContent(stored.data)) {
      this.draftReady = true;
      return;
    }
    if (serverUpdatedAt && stored.savedAt <= Date.parse(serverUpdatedAt)) {
      this.draftStore!.clear();
      this.draftReady = true;
      return;
    }

    const d = stored.data;
    const when = new Date(stored.savedAt).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const lines = d.prnItems?.length ?? 0;
    const details = [
      d.vendor?.name ? `vendor <strong>${escapeHtml(d.vendor.name)}</strong>` : '',
      d.invoiceNumber ? `invoice <strong>${escapeHtml(d.invoiceNumber)}</strong>` : '',
      `<strong>${lines}</strong> line${lines === 1 ? '' : 's'}`,
    ].filter(Boolean);
    const choice = await this.confirmDialog.choose({
      title: 'Restore unsaved PRN?',
      message:
        `This PRN was not saved. A copy from ${when} is still on this computer: ${details.join(', ')}.` +
        '<br><small class="text-muted">"Not now" keeps the copy, but it is replaced once you start entering a PRN here.</small>',
      confirmText: 'Restore',
      altText: 'Discard',
      cancelText: 'Not now',
      size: 'lg',
    });
    if (choice === 'confirm') this.applyDraft(d);
    else if (choice === 'alt') this.draftStore!.clear();
    this.draftReady = true;
  }

  private applyDraft(d: PrnDraftSnapshot): void {
    // Make sure the vendor picker can show the restored value.
    if (d.vendor?.id && !this.vendors.some((v: any) => v.id === d.vendor.id)) {
      this.vendors = [...this.vendors, d.vendor];
    }

    // Assigned directly, not via onVendorSelected: that clears the lines.
    this.selectedVendorId = d.selectedVendorId;
    this.selectedPoNumber = d.selectedPoNumber;
    this.selectedPoData = d.selectedPoData;
    this.invoiceNumber = d.invoiceNumber;
    this.invoiceDate = d.invoiceDate;
    this.returnDate = d.returnDate;
    this.freightCharges = d.freightCharges;
    this.packingCharges = d.packingCharges;
    this.otherDiscounts = d.otherDiscounts;
    this.overallDiscountAmount = d.overallDiscountAmount;
    this.prnItems = d.prnItems ?? [];
    this.selectedItems.clear();

    this.prnSummary.otherDiscounts = Number(this.otherDiscounts) || 0;
    this.updatePrnSummary();
    if (d.roundOff != null && d.roundOff !== this.prnSummary.roundOff) {
      this.prnSummary.roundOff = d.roundOff;
      this.prnSummary.finalTotal = d.finalTotal;
    }
    this.toastr.success(
      `${this.prnItems.length} line(s) restored. Save Draft to keep them on the server.`,
      'PRN restored',
    );
  }

  get selectedQuantity(): number {
    return this._selectedQuantity;
  }

  set selectedQuantity(value: number) {
    this._selectedQuantity = value;

    if (this.soh && value > this.soh) {
      this.toastr.warning(
        `Quantity ${value} exceeds available stock (${this.soh}).`,
      );
    }
  }
  onReturnedQuantityChange(item: any): void {
    const itemSOH = item.productVariant?.soh || item.soh || 0;

    // Validate the current value
    if (item.returnedQuantity > itemSOH) {
      this.toastr.error(
        `Cannot return ${item.returnedQuantity} units of ${item.skuCode}. Maximum available stock is ${itemSOH}.`,
        'Invalid Quantity',
      );

      // Reset to maximum allowed
      item.returnedQuantity = itemSOH;

      this.toastr.info(
        `Quantity adjusted to maximum available stock: ${itemSOH}`,
        'Quantity Corrected',
      );
      return;
    }

    this.updatePrnSummary();
  }

  // Add these methods to your CreatePrnComponent class

  // Method to handle cost price selection and auto-select corresponding MRP
  onCostPriceSelected(selectedCostPrice: number) {
    if (!selectedCostPrice || !this.selectedProduct) return;

    this.enteredCostPrice = selectedCostPrice;

    // Find the corresponding MRP for the selected cost price
    const correspondingPricing = this.findPricingByCostPrice(selectedCostPrice);
    if (correspondingPricing) {
      this.selectedMRP = correspondingPricing.mrp;
    }
  }

  // Method to handle MRP selection and auto-select corresponding cost price
  onMRPSelected(selectedMRP: number) {
    if (!selectedMRP || !this.selectedProduct) return;

    this.selectedMRP = selectedMRP;

    // Find the corresponding cost price for the selected MRP
    const correspondingPricing = this.findPricingByMRP(selectedMRP);
    if (correspondingPricing) {
      this.enteredCostPrice = correspondingPricing.costPrice;
    }
  }

  // Helper method to find pricing by cost price
  private findPricingByCostPrice(costPrice: number): any | null {
    if (!this.selectedProduct?.variants) return null;

    for (const variant of this.selectedProduct.variants) {
      if (variant.pricing) {
        const pricing = variant.pricing.find(
          (p: any) => +p.costPrice === +costPrice,
        );
        if (pricing) return pricing;
      }
    }
    return null;
  }

  // Helper method to find pricing by MRP
  private findPricingByMRP(mrp: number): any | null {
    if (!this.selectedProduct?.variants) return null;

    for (const variant of this.selectedProduct.variants) {
      if (variant.pricing) {
        const pricing = variant.pricing.find((p: any) => +p.mrp === +mrp);
        if (pricing) return pricing;
      }
    }
    return null;
  }

  // Method to handle cost price changes in the PRN table
  onTableCostPriceChange(item: any, newCostPrice: number) {
    if (!newCostPrice || !item.productVariant) return;

    item.costPrice = newCostPrice;

    // Find corresponding MRP for the new cost price
    const correspondingPricing = item.productVariant.pricing?.find(
      (p: any) => +p.costPrice === +newCostPrice,
    );

    if (correspondingPricing) {
      item.mrp = correspondingPricing.mrp;
      item.sellingPrice = correspondingPricing.sellingPrice;
    }

    // Trigger existing price change calculations
    this.onCostPriceChange(item);
  }

  // Method to handle MRP changes in the PRN table
  onTableMRPChange(item: any, newMRP: number) {
    if (!newMRP || !item.productVariant) return;

    item.mrp = newMRP;

    // Find corresponding cost price for the new MRP
    const correspondingPricing = item.productVariant.pricing?.find(
      (p: any) => +p.mrp === +newMRP,
    );

    if (correspondingPricing) {
      item.costPrice = correspondingPricing.costPrice;
      item.sellingPrice = correspondingPricing.sellingPrice;

      // Recalculate actual cost price and other values
      const gstPercent = +item.gstPercent || 0;
      item.actualCostPrice = +(item.costPrice / (1 + gstPercent / 100)).toFixed(
        4,
      );

      // Trigger existing price change calculations
      this.onPriceChange(item, 'costPrice');
    }
  }

  // Updated populatePriceOptions method to create sorted price-MRP pairs
  populatePriceOptions(products: any) {
    this.allCostPrices = [];
    this.allMRPs = [];
    this.currentMRP = 0;

    // The API returns active pricing ordered by updatedAt desc, so the first row
    // of the first variant that has any is the live price.
    const current = (products?.variants || []).find(
      (v: any) => v?.pricing?.length,
    )?.pricing?.[0];
    this.currentMRP = +current?.mrp || 0;

    if (products?.variants && products?.variants?.length) {
      // Create Sets to avoid duplicates
      const costPriceSet = new Set<number>();
      const mrpSet = new Set<number>();

      products.variants.forEach((variant: any) => {
        if (variant.pricing && variant.pricing.length) {
          variant.pricing.forEach((price: any) => {
            const costPriceNum = price.costPrice ? +price.costPrice : 0;
            const mrpNum = price.mrp ? +price.mrp : 0;

            if (costPriceNum > 0) {
              costPriceSet.add(costPriceNum);
            }
            if (mrpNum > 0) {
              mrpSet.add(mrpNum);
            }
          });
        }
      });

      // Keep the API order: most recently updated price first.
      this.allCostPrices = Array.from(costPriceSet);
      this.allMRPs = Array.from(mrpSet);
    }
  }

  loadPreview(id: string) {
    this.showPreview = true;
    this.isLoadingPreview = true;

    this.prnService.downloadPrn(id).subscribe({
      next: (response: any) => {
        const blob = response.body;
        if (!blob || blob.size === 0) {
          console.error('Empty PDF blob received');
          this.toastr.error('Received empty PDF');
          this.isLoadingPreview = false;
          return;
        }
        const url = URL.createObjectURL(blob);
        this.rawBlobUrl = url;
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          url + '#toolbar=0&navpanes=0&view=FitH',
        );
        this.isLoadingPreview = false;
      },
      error: (err) => {
        console.error('Failed to download PRN PDF', err);
        this.toastr.error('Failed to load preview');
        this.isLoadingPreview = false;
      },
    });
  }

  closePreview() {
    this.showPreview = false;
    // Clean up blob URL
    if (this.rawBlobUrl) {
      URL.revokeObjectURL(this.rawBlobUrl);
      this.rawBlobUrl = null;
    }
    this.router.navigate(['/prn']);
  }

  downloadPdf() {
    if (!this.rawBlobUrl) return;
    const link = document.createElement('a');
    link.href = this.rawBlobUrl;
    link.download = `PRN-${this.prnNumber || 'document'}.pdf`;
    link.click();
  }

  printPdf() {
    if (!this.rawBlobUrl) return;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = this.rawBlobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.print();
      // Optional: remove iframe after small delay
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
  }
}
